import { ChevronDown } from "lucide-react"
import React, { useState, useEffect } from "react"
import { useBooking } from "@/context/booking"

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isSticky, setIsSticky] = useState(false)
  const [currentPath, setCurrentPath] = useState("")

  useEffect(() => {
    setCurrentPath(window.location.pathname)

    const handleScroll = () => {
      setIsSticky(window.scrollY > 300)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const { openModal } = useBooking()

  const navigateWithHash = (hash: string) => {
    if (window.location.pathname === "/") {
      window.location.hash = hash
    } else {
      window.location.href = `/${hash ? `#${hash}` : ""}`
    }
  }

  const menuItems = [
    // {
    //   title: "Về Thiền Trúc",
    //   href: "/",
    //   submenu: [{ title: "Hơi thở Thiền Trúc", href: "/thien-truc-breath" }],
    // },
    {
      title: "Hương vị",
      href: "/#green",
      submenu: [
        {
          title: "Trà xanh lài",
          href: "/#green",
          onClick: () => navigateWithHash("green"),
        },
        {
          title: "Trà đen",
          href: "/#black",
          onClick: () => navigateWithHash("black"),
        },
      ],
    },
    {
      title: "Hướng dẫn",
      href: "/#guide",
      submenu: [],
      onClick: () => navigateWithHash("guide"),
    },
  ].map(item => ({
    ...item,
    active: item.href === currentPath,
  }))

  const isMenuOverviewPage =
    currentPath === "/thuc-don-tan-dinh" ||
    currentPath === "/thuc-don-thu-duc" ||
    currentPath === "/dia-diem"
  const isLocationPage = currentPath === "/dia-diem"
  const useWhiteBranding = !isSticky && (isMenuOverviewPage || isLocationPage)

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 font-semibold text-xl backdrop-blur-md
    ${
      isSticky
        ? "bg-[rgba(254,245,234,0.6)] backdrop-saturate-150 shadow-[0_4px_20px_rgba(0,0,0,0.05)] border-b border-white/20"
        : "bg-transparent backdrop-blur-0 border-b border-transparent"
    }`}
      style={{
        WebkitBackdropFilter: isSticky ? "blur(16px)" : "blur(0px)",
        transition: "all 0.45s ease",
      }}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between py-4 transition-all duration-300">
          {/* Logo */}
          <div className="flex items-center transition-all duration-300">
            <a
              href="/"
              className={`transition-opacity duration-500 ${
                isSticky ? "opacity-0 absolute" : "opacity-100"
              }`}
            >
              <img
                width="144"
                height="82"
                src={
                  useWhiteBranding
                    ? "/logos/web-TT-logo-white.png"
                    : "./logos/logo.png"
                }
                className="h-12 sm:h-16 w-auto drop-shadow-[0_1px_2px_rgba(0,0,0,0.15)]"
                alt="Thien Truc Logo"
              />
            </a>

            <a
              href="/"
              className={`transition-opacity duration-500 ${
                isSticky ? "opacity-100" : "opacity-0 absolute"
              }`}
            >
              <img
                width="144"
                height="82"
                src="./logos/logo.png"
                className="h-12 sm:h-16 w-auto"
                alt="Thien Truc Logo"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-10">
            <ul className="flex items-center space-x-8">
              {menuItems.map((item, index) => (
                <li key={index} className="relative group">
                  <a
                    href={item.href}
                    className={`flex items-center space-x-1 py-2 transition-all duration-300 ${
                      isSticky
                        ? "text-[#115544]/90 hover:text-[#115544]"
                        : isMenuOverviewPage
                        ? "text-white hover:text-white/90"
                        : "text-[#115544] hover:text-[#115544]/80"
                    }`}
                    onClick={e => {
                      if (item.onClick) {
                        e.preventDefault()
                        item.onClick()
                      }
                    }}
                  >
                    <span>{item.title}</span>
                    {item.submenu.length > 0 && (
                      <ChevronDown className="size-4 mt-[2px] transition-transform duration-300 group-hover:rotate-180" />
                    )}
                  </a>

                  {/* Dropdown */}
                  {item.submenu.length > 0 && (
                    <ul
                      className={`absolute top-full left-0 mt-6 min-w-max rounded-xl backdrop-blur-xl
                    shadow-[0_8px_30px_rgba(0,0,0,0.08)]
                    border border-white/10 transition-all duration-300 transform
                    ${
                      isSticky
                        ? "bg-[rgba(254,245,234,0.8)]"
                        : "bg-[rgba(255,255,255,0.1)]"
                    }
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible group-hover:translate-y-1`}
                    >
                      {item.submenu.map((sub, i) => (
                        <li key={i}>
                          <a
                            href={sub.href}
                            className="block px-6 py-3 text-[#115544] text-sm font-medium hover:bg-white/30 rounded-xl hover:text-[#115544]/90 transition-all duration-200"
                            onClick={e => {
                              if ((sub as any).onClick) {
                                e.preventDefault()
                                ;(sub as any).onClick()
                              }
                            }}
                          >
                            {sub.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full bg-white/40 backdrop-blur-sm border border-white/30 shadow-sm hover:bg-white/60 transition-all duration-300"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Menu Toggle"
          >
            <svg
              className={`w-6 h-6 ${
                useWhiteBranding ? "stroke-white" : "text-[#115544]"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </header>
  )
}

export default Header
